import {User} from '@app/layout/admin/services/api/user-interfaces';
import {CollectionUtils} from '@app/shared/util/collections/collection-utils';
import {forkJoin, isObservable, map, Observable, of, shareReplay} from 'rxjs';
import {TicketsService} from '../tickets.service';

export class UsersCache {
  private readonly usersCache: Map<number, Map<string, User> | Observable<Map<number, Map<string, User>>>> = new Map();

  constructor(
    private ticketService: TicketsService,
  ) {}

  getUsersForSites(siteIds: number[]): Observable<Map<number, Map<string, User>>> {
    const uncachedSiteIds = new Set<number>();
    const cachedSiteUsersMap = new Map<number, Map<string, User>>();
    const siteUsersFetches = new Set<Observable<Map<number, Map<string, User>>>>();
    siteIds.forEach(siteId => {
      const siteUsers = this.usersCache.get(siteId);
      if (isObservable(siteUsers)) {
        siteUsersFetches.add(siteUsers);
      } else if (siteUsers) {
        cachedSiteUsersMap.set(siteId, siteUsers);
      } else {
        uncachedSiteIds.add(siteId);
      }
    });
    if (uncachedSiteIds.size) {
      siteUsersFetches.add(this.fetchAndCacheUsersFromAPI(Array.from(uncachedSiteIds)));
    }

    return siteUsersFetches.size
      ? forkJoin(Array.from(siteUsersFetches)).pipe(
          map((usersByIdBySiteIdsList: Map<number, Map<string, User>>[]) =>{
            usersByIdBySiteIdsList.forEach(usersByIdBySiteIds => 
              usersByIdBySiteIds.forEach((usersById, siteId) =>
                cachedSiteUsersMap.set(siteId, usersById)));
            return cachedSiteUsersMap;
          }))
      : of(cachedSiteUsersMap);
  }

  private fetchAndCacheUsersFromAPI(siteIds: number[]): Observable<Map<number, Map<string, User>>> {
    const userFetch$ = this.ticketService.getAssigneesForSites(siteIds).pipe(
      map((usersBySiteId => this.populateUsersCache(usersBySiteId))),
      shareReplay(1)
    );
    siteIds.forEach(siteId => this.usersCache.set(siteId, userFetch$));
    return userFetch$;
  }

  private populateUsersCache(usersBySiteId: Map<number, User[]>): Map<number, Map<string, User>> {
    const usersByIdBySiteId = new Map<number, Map<string, User>>();
    usersBySiteId.forEach((users, siteId) => {
      const usersById = CollectionUtils.indexBy(users, 'id');
      usersByIdBySiteId.set(siteId, usersById);
      this.usersCache.set(siteId, usersById);
    });
    return usersByIdBySiteId;
  }
}
